import { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/pages/Categories";
import { ExternalLink, Loader2, Calendar, Newspaper, FileText, TrendingUp, RefreshCw } from "lucide-react";

const CATEGORIES = [
  { slug: "trade-news", label: "Trade News", icon: Newspaper, blurb: "Latest news on India trade, exports, tariffs, ports and shipping." },
  { slug: "trade-agreements", label: "Trade Agreements", icon: FileText, blurb: "FTA progress, CEPA, EU & GCC trade-deal coverage." },
  { slug: "market-insights", label: "Market Insights", icon: TrendingUp, blurb: "India manufacturing, supply-chain and industry analysis." },
];

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

function timeAgo(iso) {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch {
    return "";
  }
}

export default function Insights() {
  const navigate = useNavigate();
  const location = useLocation();
  const initial = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const c = params.get("category");
    return CATEGORIES.find((x) => x.slug === c)?.slug || "trade-news";
  }, [location.search]);

  const [active, setActive] = useState(initial);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => { setActive(initial); }, [initial]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get("/insights", { params: { category: active, limit: 20 } });
        if (!cancelled) setItems(data.items || []);
      } catch (e) {
        // Keep last-good items on transient errors so a 5xx blip doesn't wipe the grid.
        if (!cancelled) setError(e.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [active]);

  const onTab = (slug) => {
    setActive(slug);
    const p = new URLSearchParams(location.search);
    p.set("category", slug);
    navigate(`/insights?${p.toString()}`, { replace: true });
  };

  const current = CATEGORIES.find((c) => c.slug === active);

  return (
    <div data-testid="insights-page">
      <PageHeader
        eyebrow="INSIGHTS"
        title="Trade intelligence for procurement teams."
        sub="Curated news, trade agreement coverage and market analysis on India and its trade corridors — updated daily."
      />
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-24">
        {/* Tabs */}
        <div className="border-b border-white/5 flex flex-wrap gap-1 mb-8" role="tablist" data-testid="insights-tabs">
          {CATEGORIES.map((c) => {
            const isActive = c.slug === active;
            return (
              <button
                key={c.slug}
                onClick={() => onTab(c.slug)}
                role="tab"
                aria-selected={isActive}
                data-testid={`tab-${c.slug}`}
                className={`flex items-center gap-2 px-4 py-3 text-[13px] font-medium border-b-2 -mb-px transition-colors ${
                  isActive
                    ? "text-amber-300 border-amber-400"
                    : "text-zinc-400 hover:text-white border-transparent hover:border-white/10"
                }`}
              >
                <c.icon className="h-4 w-4" />
                {c.label}
              </button>
            );
          })}
        </div>

        <div className="mb-6 text-zinc-400 text-[14px] max-w-2xl">{current?.blurb}</div>

        {loading ? (
          <ArticleSkeleton />
        ) : items.length === 0 && error ? (
          <div className="border border-red-500/30 bg-red-500/[0.04] rounded-lg p-6 text-red-300 text-sm" data-testid="insights-error">
            Couldn&apos;t load articles right now. {error}
          </div>
        ) : items.length === 0 ? (
          <div className="border border-white/5 bg-zinc-900/40 rounded-lg p-10 text-center text-zinc-400" data-testid="insights-empty">
            <RefreshCw className="h-5 w-5 mx-auto mb-3 text-zinc-500" />
            No articles indexed for this category yet. The scheduler runs twice daily — please check back shortly.
          </div>
        ) : (
          <>
            {error && (
              <div className="mb-4 border border-amber-400/30 bg-amber-400/[0.05] rounded-md px-4 py-2.5 text-[13px] text-amber-200 flex items-center gap-2" data-testid="insights-stale">
                Couldn&apos;t refresh — showing the last known articles. ({error})
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="insights-grid">
              {items.map((a, i) => (
                <ArticleCard key={a.url} a={a} idx={i} />
              ))}
            </div>
          </>
        )}

        <div className="mt-10 text-[12px] text-zinc-500 leading-relaxed border-t border-white/5 pt-6">
          News is aggregated from public sources via NewsData.io. Articles link to the original publisher. VYR Trade does not endorse the views expressed in third-party reporting.
        </div>
      </section>
    </div>
  );
}

function ArticleCard({ a, idx }) {
  const [imgFailed, setImgFailed] = useState(false);
  const showImg = a.image_url && !imgFailed;
  return (
    <a
      href={a.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group border border-white/5 bg-zinc-900/40 hover:border-white/15 hover:bg-zinc-900 rounded-lg overflow-hidden flex flex-col transition-colors"
      data-testid={`article-${idx}`}
    >
      <div className="relative aspect-[16/9] bg-zinc-950 overflow-hidden">
        {showImg ? (
          <img
            src={a.image_url}
            alt=""
            referrerPolicy="no-referrer"
            loading="lazy"
            onError={() => setImgFailed(true)}
            className="h-full w-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
          />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-zinc-900 to-zinc-950 grid place-items-center">
            <Newspaper className="h-8 w-8 text-zinc-700" />
          </div>
        )}
        <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-zinc-950/80 backdrop-blur-sm border border-white/10 rounded-md px-2 py-1">
          <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse-dot" />
          <span className="font-mono text-[10px] tracking-[0.18em] text-zinc-300 uppercase">
            {a.source_name || "External"}
          </span>
        </div>
      </div>
      <div className="p-5 flex flex-col gap-3 flex-1">
        <h3 className="font-display text-[17px] font-semibold leading-tight text-white group-hover:text-amber-300 transition-colors">
          {a.title || "(Untitled)"}
        </h3>
        {a.description && (
          <p className="text-[13.5px] text-zinc-400 leading-relaxed line-clamp-3">{a.description}</p>
        )}
        <div className="mt-auto flex items-center justify-between gap-3 pt-2">
          <div className="flex items-center gap-1.5 text-[11.5px] text-zinc-500 font-mono">
            <Calendar className="h-3 w-3" />
            <span>{formatDate(a.published_at)}</span>
            <span className="text-zinc-700">·</span>
            <span>{timeAgo(a.published_at)}</span>
          </div>
          <span className="inline-flex items-center gap-1 text-[12px] text-amber-400 group-hover:text-amber-300">
            Read <ExternalLink className="h-3 w-3" />
          </span>
        </div>
      </div>
    </a>
  );
}

function ArticleSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="border border-white/5 bg-zinc-900/40 rounded-lg overflow-hidden">
          <div className="aspect-[16/9] bg-zinc-900/60 animate-pulse" />
          <div className="p-5 space-y-3">
            <div className="h-4 bg-zinc-800/60 rounded animate-pulse" />
            <div className="h-3 w-3/4 bg-zinc-800/60 rounded animate-pulse" />
            <div className="h-3 bg-zinc-800/60 rounded animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}
